import { Box, Button, Divider, Typography, useMediaQuery, CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import color from "../../components/utils/Colors";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "@mui/icons-material";
import CircularProgressWithLabel from "../../components/utils/CircularProgressWithLabel";
import CustomGraph from "../../components/utils/CustomGraph";
import { useUser } from "../../services/Context/UserContext";
import {
  ChildProfile,
  fetchChildProgress,
  fetchParentDashboard,
  shiftMonth,
} from "../../services/adminData";

const ChildProgress: React.FC = () => {
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [children, setChildren] = useState<ChildProfile[]>([]);
  const [activeChildId, setActiveChildId] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState<Awaited<
    ReturnType<typeof fetchChildProgress>
  > | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    fetchParentDashboard(user.id).then(({ children: childList }) => {
      setChildren(childList);
      const stateChildId = (location.state as any)?.childId;
      const initialChildId = stateChildId
        ? Number(stateChildId)
        : childList[0]?.id;
      if (initialChildId) setActiveChildId(Number(initialChildId));
    });
  }, [user?.id, location.state]);

  useEffect(() => {
    if (!activeChildId) {
      setLoading(false);
      return;
    }
    const childName = children.find((child) => child.id === activeChildId)?.name;
    setLoading(true);
    fetchChildProgress(activeChildId, selectedMonth, childName)
      .then(setProgressData)
      .finally(() => setLoading(false));
  }, [activeChildId, selectedMonth, children]);

  const activeChildName =
    children.find((child) => child.id === activeChildId)?.name ||
    progressData?.childName ||
    "Child";

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleDateChange = (direction: string) => {
    setSelectedMonth((current) =>
      shiftMonth(current, direction === "previous" ? "previous" : "next"),
    );
  };

  return (
    <Box
      sx={{
        p: 4,
        pt: 2,
        width: isSmallScreen ? "100%" : "80%",
        margin: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        onClick={handleGoBack}
        sx={{
          margin: 0,
          position: "absolute",
          top: isSmallScreen ? "120px" : "140px",
          color: color.firstColor,
          fontSize: "16px",
          fontWeight: "bold",
          left: isSmallScreen ? 10 : 60,
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <ChevronLeft /> Back
      </Typography>

      <Typography
        sx={{
          color: color.firstColor,
          fontSize: isSmallScreen ? "14px" : "26px",
          fontWeight: "bold",
          textAlign: "center",
          width: "100%",
          mb: 4,
          mt: 1.5,
          pb: 3,
          borderBottom: "solid 2px",
          borderColor: color.borderColor,
        }}
      >
        G2I Parent Profile
      </Typography>

      <Box>
        <h2
          style={{
            color: color.textColor,
            textAlign: "center",
            width: "100%",
            paddingLeft: "10px",
            margin: "0px",
          }}
        >
          Child Profiles
        </h2>

        {children.length === 0 ? (
          <Typography sx={{ textAlign: "center", color: "#666", mt: 4 }}>
            No child profiles available.
          </Typography>
        ) : (
          <Box
            sx={{
              my: 4,
              mt: 4,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 4 },
              boxShadow: "none",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {children.map((child) => (
              <Button
                key={child.id}
                onClick={() => setActiveChildId(child.id)}
                sx={{
                  backgroundColor:
                    activeChildId === child.id
                      ? color.firstColor
                      : "transparent",
                  color:
                    activeChildId === child.id ? "white" : color.firstColor,
                  border: "solid 2px",
                  borderColor: color.borderColor,
                  "&:hover": {
                    backgroundColor: color.firstColor,
                    color: "white",
                  },
                  minWidth: "150px",
                  textTransform: "none",
                  display: "flex",
                  borderRadius: 2,
                  py: 1,
                  fontWeight: "bold",
                }}
              >
                {child.name}
              </Button>
            ))}
          </Box>
        )}

        <Box
          sx={{
            p: 2,
            pb: 4,
            borderRadius: 2,
            border: "solid 1.5px",
            borderColor: color.borderColor,
            background: "white",
          }}
        >
          <Typography sx={{ color: color.textColor, fontSize: "24px" }}>
            {activeChildName} - Enrolled Course progress
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress sx={{ color: color.firstColor }} />
            </Box>
          ) : (progressData?.progressCourses.length ?? 0) === 0 ? (
            <Typography sx={{ color: "#666", mt: 3 }}>
              No enrolled courses for this child yet.
            </Typography>
          ) : (
            <Box
              sx={{
                my: 3,
                mb: 8,
                display: "flex",
                width: "100%",
                flexWrap: "wrap",
                gap: 8,
                justifyContent: "space-around",
              }}
            >
              {progressData?.progressCourses.map((course) => (
                <CircularProgressWithLabel
                  key={course.id}
                  size={160}
                  fontSize={22}
                  textcolor={"#2e0b57"}
                  circlecolor={"#2e0b57"}
                  value={course.progress}
                  label={course.title}
                />
              ))}
            </Box>
          )}

          <Divider
            sx={{
              border: "solid 1.5px",
              borderColor: color.borderColor,
              my: 3,
            }}
          />

          <Typography sx={{ color: color.textColor, fontSize: "24px", mb: 4 }}>
            Total time spent on courses
          </Typography>

          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
            }}
          >
            {progressData && progressData.graphData.length > 0 ? (
              <CustomGraph
                graphData={progressData.graphData}
                weeksData={progressData.weeksData}
                labels={progressData.labels}
                date={progressData.periodLabel}
                onDateChange={handleDateChange}
              />
            ) : (
              <Typography sx={{ color: "#666" }}>
                No activity data for this period.
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", width: "100%", mt: 4, mb: 4 }}>
          <Button
            onClick={() => navigate("/")}
            id="button1"
            sx={{
              borderRadius: "4px",
              margin: "auto",
              mt: 4,
              fontSize: "18px",
            }}
          >
            Home
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ChildProgress;
